// web/src/utils/subscription.ts

export interface MinimalUser {
  role?: string;
  plan?: string;
  subscription_status?: string;
}

export interface MinimalLeak {
  is_locked?: boolean;
  leak_rank?: number;
}

/**
 * Checks if a user is on the Free tier.
 * Internal roles (admin, super_admin, analyst) and paid plans (Business Pro, Enterprise)
 * are considered subscribed and are NOT on the free tier.
 */
export function isFreePlanUser(user: MinimalUser | null | undefined): boolean {
  if (!user) return true;
  const role = (user.role || "").toLowerCase();
  if (role === "admin" || role === "super_admin" || role === "analyst") {
    return false;
  }
  const plan = (user.plan || "").toLowerCase().replace(/[-_ ]/g, "");
  if (
    plan.includes("pro") ||
    plan.includes("business") ||
    plan.includes("enterprise") ||
    plan.includes("custom")
  ) {
    return false;
  }
  return true;
}

/**
 * Evaluates whether a leak is locked for the given user and position.
 * Rule:
 * 1. If searching, free tier is locked from the first leak itself.
 * 2. In normal feed, first 50 leaks are completely free. Leaks after 50 are locked for free tier users.
 */
export function isLeakLocked(
  leak: MinimalLeak | null | undefined,
  rank: number,
  user: MinimalUser | null | undefined,
  isSearching: boolean = false
): boolean {
  // Paid or staff users never encounter locked leaks
  if (!isFreePlanUser(user)) {
    return false;
  }

  // If backend explicitly marked it as locked
  if (leak?.is_locked === true) {
    return true;
  }

  // Search is a paid/pro feature: locked from the first leak itself
  if (isSearching) {
    return true;
  }

  // Determine effective rank (1-indexed order in feed)
  const effectiveRank = (leak && typeof leak.leak_rank === "number" && leak.leak_rank > 0)
    ? leak.leak_rank
    : rank;

  // After 50 leaks in normal feed, lock for free users
  return effectiveRank > 50;
}
